import _ from 'lodash';
import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').Appointment} Appointment
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetAppointmentsActionOpts
 * @property {string} organization
 * @property {string} target
 * @property {number} startAt
 * @property {number} endAt
 * @property {boolean} [commitToStore=true]
 * @property {number} skip
 * @property {number} [limit=20]
 *
 * @callback GetAppointmentsAction
 * @param {ActionContext} ctx
 * @param {GetAppointmentsActionOpts} opts
 * @returns {Promise<Appointment>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetAppointmentsAction}
 */
export const getAppointments = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ limit: 20, commitToStore: true }, opts);
  if (!opts.organization) throw new Error(`${nms}: Organization filter is required`);

  // fetch from sdk
  const query = {
    organization: opts.organization,
    $populate: {
      patient: {
        service: 'personal-details',
        key: 'meta.patient',
        $select: [ 'id', 'picURL', 'name', 'insuranceCards', 'tags' ]
      }
    }
  };
  if (opts.target) query.target = opts.target;
  if (opts.startAt) query.startAt = { $gte: opts.startAt };
  if (opts.endAt) query.endAt = { $lte: opts.endAt };

  if (typeof opts.limit === 'number') query.$limit = opts.limit;
  if (typeof opts.skip === 'number') query.$skip = opts.skip;

  const result = await sdk.appointments().find(query);
  const appointments = result.items.map(({ $populated, ...i }) => ({ ...i, ...$populated }));

  if (opts.commitToStore === true) ctx.commit('updateInAppointments', { items: appointments, upsert: true });

  return Object.assign(result, { items: appointments });
};

/**
 * @typedef {Object} CreateAppointmentActionOpts
 * @property {boolean} $addToStore add created appointment to store
 * @property {number} startAt
 * @property {number} endAt
 * @property {string} reason
 * @property {string} organization
 * @property {string} target
 * @property {Object.<string,any>} meta
 *
 * @callback CreateAppointmentAction
 * @param {ActionContext} ctx
 * @param {CreateAppointmentActionOpts} opts
 * @returns {Promise<Appointment>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateAppointmentAction}
 */
export const createAppointment = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const { $addToStore, ...data } = opts;
  if (_.isEmpty(data)) throw new Error(`${nms}: Invalid create op payload`);

  // fetch from sdk
  const appointment = await sdk.appointments().create(data);
  if (appointment.meta && appointment.meta.patient) {
    appointment.patient = await sdk.personalDetails().get(appointment.meta.patient, {
      query: { $select: [ 'id', 'picURL', 'name', 'insuranceCards', 'tags' ] }
    });
  }
  if ($addToStore) ctx.commit('updateInAppointments', { items: [ appointment ], upsert: true });

  return appointment;
};

/**
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @param {ActionContext} ctx
 * @param {string} id
 * @param {Object.<string,any>} data
 * @returns {Promise<Appointment>}
 */
async function updateAppointment (store, sdk, ctx, id, data) {
  if (!id) throw new Error(`${nms}: Id required`);
  if (!data) throw new Error(`${nms}: Update payload required`);
  if (_.isEmpty(data)) throw new Error(`${nms}: Invalid pdate payload`);

  // execute from sdk
  const appointment = await sdk.appointments().update(id, data);
  // inherit patient details (if cached)
  if (appointment && appointment.meta && appointment.meta.patient) {
    const existing = ctx.state.appointments.find(a => a.id === id);
    appointment.patient = (existing && existing.patient) || await sdk.personalDetails().get(appointment.meta.patient, {
      query: { $select: [ 'id', 'picURL', 'name', 'insuranceCards', 'tags' ] }
    });
  }
  if (appointment) ctx.commit('updateInAppointments', { items: [ appointment ] });

  return appointment;
}

/**
 * @typedef {Object} ConfirmAppointmentActionOpts
 * @property {string} id
 *
 * @callback ConfirmAppointmentAction
 * @param {ActionContext} ctx
 * @param {ConfirmAppointmentActionOpts} opts
 * @returns {Promise<Appointment>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {ConfirmAppointmentAction}
 */
export const confirmAppointment = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  return updateAppointment(store, sdk, ctx, opts.id, { arrive: true });
};

/**
 * @typedef {Object} CancelAppointmentActionOpts
 * @property {string} id
 *
 * @callback CancelAppointmentAction
 * @param {ActionContext} ctx
 * @param {CancelAppointmentActionOpts} opts
 * @returns {Promise<Appointment>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CancelAppointmentAction}
 */
export const cancelAppointment = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  return updateAppointment(store, sdk, ctx, opts.id, { cancel: true });
};
